import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { CmsContentWorkflowStatus } from '@ndvr/cms-api';

import { Button } from '@/components/ui/button';
import { MARKETING_CMS_SITES_PATH, marketingCmsReviewPath } from '@/views/marketing/marketing-routes';
import { useAuthContext } from 'views/auth/Auth';

import { useCmsPermissions } from './cms-permissions';
import { useCmsHistory, useCmsMicrosites } from './cms-queries';
import { CmsPageHeader, CmsRelativeDateTime, CmsStatusBadge } from './cms-ui';
import { deriveCmsVersionRows } from './cms-version-state';

interface PendingReviewRowProps {
    microsite: NonNullable<ReturnType<typeof useCmsMicrosites>['data']>[number];
    onResolved: (cmsMicrositeGUID: string, pendingCount: number, isSettled: boolean) => void;
}

const PendingReviewRows = ({ microsite, onResolved }: PendingReviewRowProps) => {
    const historyQuery = useCmsHistory(microsite.cmsMicrositeGUID);
    const pendingRows = useMemo(
        () =>
            deriveCmsVersionRows(historyQuery.data, {
                publishedContentVersionGUID: microsite.publishedContentVersionGUID,
            }).filter((row) => row.status === CmsContentWorkflowStatus.READY_FOR_REVIEW),
        [historyQuery.data, microsite.publishedContentVersionGUID],
    );

    useEffect(() => {
        onResolved(microsite.cmsMicrositeGUID, pendingRows.length, !historyQuery.isLoading && !historyQuery.isError);
    }, [historyQuery.isError, historyQuery.isLoading, microsite.cmsMicrositeGUID, onResolved, pendingRows.length]);

    if (historyQuery.isLoading) {
        return (
            <tr className="hover:bg-muted/40 h-[60px] border-t align-middle">
                <td className="px-2" colSpan={6}>
                    <span className="text-muted-foreground text-sm">Loading {microsite.siteName} versions...</span>
                </td>
            </tr>
        );
    }

    if (historyQuery.isError) {
        return (
            <tr className="hover:bg-muted/40 h-[60px] border-t align-middle">
                <td className="px-2" colSpan={6}>
                    <span className="text-destructive text-sm">Unable to load {microsite.siteName} versions.</span>
                </td>
            </tr>
        );
    }

    return (
        <>
            {pendingRows.map((row) => (
                <tr
                    key={row.version.cmsContentVersionGUID}
                    className="hover:bg-muted/40 h-[60px] border-t align-middle"
                >
                    <td className="overflow-hidden px-2 text-ellipsis whitespace-nowrap">
                        {microsite.advisorDisplayName}
                    </td>
                    <td className="overflow-hidden px-2 text-ellipsis whitespace-nowrap">
                        <div className="font-medium">{microsite.siteName}</div>
                        <div className="text-muted-foreground overflow-hidden font-mono text-xs text-ellipsis whitespace-nowrap">
                            /advisors/{microsite.slug}
                        </div>
                    </td>
                    <td className="overflow-hidden px-2 text-ellipsis whitespace-nowrap">
                        Version {row.version.versionNumber}
                    </td>
                    <td className="px-2">
                        <CmsStatusBadge status={row.displayStatus} />
                    </td>
                    <td className="overflow-hidden px-2 text-ellipsis whitespace-nowrap">
                        <CmsRelativeDateTime value={row.latestActivityAt} fallback="Not submitted" />
                    </td>
                    <td className="px-2">
                        <Button asChild variant="outline2" size="sm">
                            <Link
                                to={marketingCmsReviewPath(
                                    microsite.cmsMicrositeGUID,
                                    row.version.cmsContentVersionGUID,
                                )}
                            >
                                Review
                            </Link>
                        </Button>
                    </td>
                </tr>
            ))}
        </>
    );
};

export const CmsPendingReviews = () => {
    const permissions = useCmsPermissions();
    const { user } = useAuthContext();
    const ownerUserGUID = permissions.canReview || permissions.canAdmin ? undefined : user?.profileGUID;
    const micrositesQuery = useCmsMicrosites(ownerUserGUID);
    const microsites = micrositesQuery.data ?? [];
    const [reviewCounts, setReviewCounts] = useState<Record<string, { pendingCount: number; isSettled: boolean }>>({});
    const resolvedSiteCount = microsites.filter(
        (microsite) => reviewCounts[microsite.cmsMicrositeGUID]?.isSettled,
    ).length;
    const pendingReviewCount = microsites.reduce(
        (count, microsite) => count + (reviewCounts[microsite.cmsMicrositeGUID]?.pendingCount ?? 0),
        0,
    );
    const hasLoadedAllHistories = microsites.length > 0 && resolvedSiteCount === microsites.length;
    const showNoPendingReviews = hasLoadedAllHistories && pendingReviewCount === 0;
    const onSiteResolved = useCallback((cmsMicrositeGUID: string, pendingCount: number, isSettled: boolean) => {
        setReviewCounts((current) => ({
            ...current,
            [cmsMicrositeGUID]: { pendingCount, isSettled },
        }));
    }, []);

    return (
        <div className="flex flex-1 flex-col bg-white">
            <CmsPageHeader
                title="Pending Microsites Content Reviews"
                parent={{ label: 'Advisor Microsites', to: MARKETING_CMS_SITES_PATH }}
            />
            <section>
                <div className="pt-4">
                    {permissions.isLoading || micrositesQuery.isLoading ? (
                        <p className="text-muted-foreground px-8 text-sm">Loading pending CMS reviews...</p>
                    ) : micrositesQuery.isError ? (
                        <p className="text-destructive px-8 text-sm">Unable to load CMS reviews.</p>
                    ) : microsites.length === 0 ? (
                        <div className="mx-8 rounded-md border border-dashed p-8 text-center">
                            <p className="font-medium">No CMS sites found</p>
                            <p className="text-muted-foreground mt-1 text-sm">
                                Submitted versions will appear here when a site is ready for review.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto px-8">
                            <table className="w-full min-w-[820px] table-fixed border-collapse text-sm font-light">
                                <thead>
                                    <tr className="text-left hover:bg-transparent">
                                        <th className="text-muted-foreground h-[60px] px-2 text-xs font-medium uppercase">
                                            Advisor
                                        </th>
                                        <th className="text-muted-foreground h-[60px] px-2 text-xs font-medium uppercase">
                                            Site
                                        </th>
                                        <th className="text-muted-foreground h-[60px] px-2 text-xs font-medium uppercase">
                                            Version
                                        </th>
                                        <th className="text-muted-foreground h-[60px] px-2 text-xs font-medium uppercase">
                                            Status
                                        </th>
                                        <th className="text-muted-foreground h-[60px] px-2 text-xs font-medium uppercase">
                                            Submitted
                                        </th>
                                        <th
                                            className="text-muted-foreground h-[60px] px-2 text-xs font-medium uppercase"
                                            aria-label="Actions"
                                        />
                                    </tr>
                                </thead>
                                <tbody className="[&>tr:last-child]:border-b">
                                    {microsites.map((microsite) => (
                                        <PendingReviewRows
                                            key={microsite.cmsMicrositeGUID}
                                            microsite={microsite}
                                            onResolved={onSiteResolved}
                                        />
                                    ))}
                                    {showNoPendingReviews && (
                                        <tr className="border-t">
                                            <td className="px-2 py-8 text-center" colSpan={6}>
                                                <p className="font-medium">No versions ready for review</p>
                                                <p className="text-muted-foreground mt-1 text-sm">
                                                    Submitted CMS versions will appear here when they enter review.
                                                </p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};
